import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { DIALOG_DATA, NPSNames } from '../../../constants/dialog-data';
import { DialogAction, DialogData, Phrase } from '../../../interfaces/common-interfaces';
import { GameStateService } from '../../../services/game-state/game-state.service';
import { GameOverlayService } from '../../../services/game-overlay/game-overlay.service';
import { SignialsService } from '../../../services/signals/signials.service';
import { defer, iif, Observable, of, Subject, take, tap } from 'rxjs';
import { NpsDialogComponent } from '../../nps/nps-dialog/nps-dialog.component';
import { GameUiService } from '../../../services/game-ui/game-ui.service';
import { GameState } from '../../../services/game-ui/game-state.enum';
import { NpsDetailService } from './nps-detail.service';
import { NpsGameLoaderService } from './nps-game-loader.service';
import { GameContext } from '../../../services/game-ui/game-context.enum';
import { SPEAKERS_KEYS } from '../../../constants/textures';
import { GameAction } from '../../../services/game-ui/game-action.enum';

export interface NPSModal {
  closeModal: Observable<any>;
  showPhrase?: Observable<any>;
  finishGame?: Observable<any>;
  showSelectPhrase?: Observable<any>;
  showNewPhrase?: Observable<any>;
}
@Component({
  selector: 'app-nps-detail',
  templateUrl: './nps-detail.component.html',
  styleUrl: './nps-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NpsDetailComponent implements OnInit, OnDestroy{
  @Input()
	public detailKey: string;
	@Output()
	closed: EventEmitter<void> = new EventEmitter();
  @ViewChild('container', { read: ViewContainerRef, static: true }) container!: ViewContainerRef;

  currentPhrases: Phrase[] = [];
  showDialog = true;
  showModal = false;

  private destroy$ = new Subject<void>();
  public npsDetailData!: DialogData;

  constructor(
    private detailService: NpsDetailService,
    private loader: NpsGameLoaderService,
    private overlay: GameOverlayService,
    private ui: GameUiService,
    private signal: SignialsService,
    private cdr: ChangeDetectorRef,
    private state: GameStateService,
  ) {}

  // В компоненте с UI (не игровым полем)
  @HostListener('touchmove', ['$event'])
  onTouchMove(e: TouchEvent) {
    if (e.touches.length > 1) {
      e.preventDefault(); // Только для UI-элементов
    }
  }

  @HostListener('gesturestart', ['$event'])
  onGestureStart(e: Event) {
    e.preventDefault(); // Блокируем зум на UI
  }

  ngOnInit(): void {
    const s = this.signal.hideDialog$.subscribe(v => {
      this.showDialog = false;
      s.unsubscribe();
    });
    this.ui.setState(GameState.Dialog);
    this.npsDetailData = DIALOG_DATA[this.detailKey];
    this.currentPhrases = this.detailService.initDialog(this.npsDetailData);
    if (!this.currentPhrases.length) this.closeDialog(this.npsDetailData.action!);
  }

  updateDialog(step: string, hint?: string, sound?: string, volume?: number) {
    this.currentPhrases = this.detailService.updatePhrase(this.npsDetailData, step, hint, sound, volume);
    this.showDialog = true;
    this.ui.setState(GameState.Dialog);
    this.cdr.detectChanges();
  }

  async openMiniGame(name: string) {
    this.showDialog = false;
    
    if (!this.npsDetailData.showSpeakerInGame) {
      this.overlay.hideSpeaker();
    }
    this.ui.setState(GameState.MiniGame);
    if (this.npsDetailData.gameContext) this.ui.setContext(this.npsDetailData.gameContext);
    const ref = await this.loader.loadComponent(name, this.container);
    if (ref) {
      this.subscribeToModal(ref);
      ref.changeDetectorRef.detectChanges();
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.ui.setState(GameState.Default);
  }

  finishGame(result: any) {
    if (!result) return;
    this.state.changeGameStatus('attractions', result);
  }

  closeDetail() {
    this.ui.setState(GameState.Default);
    this.showNotepad();
    this.detailService.endDialog();
    this.overlay.hideOverlay(true);
		this.state.gamePause = false;
    this.showDialog = false;
    this.closed.emit();
  }

  closeDialog(action: DialogAction) {
    const map = {
      'startGame': () => this.openMiniGame(action.options!),
      'exitAll': () => this.closeDetail(),
      'closeDialog': () => this.finishDialog(),
      'nextDialog': () => this.nextDialog(action.options),
      'none': () => this.signal.dialogEnded.next(true),
    } as const;

    map[action.type]?.();
    this.cdr.detectChanges();
  }

  subscribeToModal(ref: ComponentRef<NPSModal>) {
    const { instance } = ref;

    const subs = [
      instance.closeModal?.subscribe(() => this.closeDetail()),
      instance.showPhrase?.subscribe((e) => this.updateDialog(e)),
      instance.showSelectPhrase?.subscribe((e) => this.updateDialog(e.dialog, e.hint, `assets-compressed/sounds/mag/${e.dialog}.mp3`)),
      instance.showNewPhrase?.subscribe((e) => this.updateDialog('custom', e.text, e.sound)),
      instance.finishGame?.subscribe((e) => {
        this.ui.setState(GameState.Dialog);
        this.ui.setContext(GameContext.None);
        this.state.changeGameStatus('attractions', e);
      })
    ];

    this.destroy$.pipe(take(1)).subscribe(() => subs.forEach(s => s?.unsubscribe()));
  }

  nextDialog(options?: string) {
    this.ui.setState(GameState.Dialog);
    if (options) {
      setTimeout(() => {
        this.currentPhrases = this.detailService.updatePhrase(this.npsDetailData, options);
        this.cdr.detectChanges();
      },100);
    } else {
      console.warn('нужно задать id следующего диалога в Action.options');
    }
  }

  finishDialog() {
    this.showDialog = false;
    if (this.npsDetailData.showSpeakerInDialog) {
      this.detailService.hideSpeaker();
    }
    this.ui.setState(GameState.MiniGame);
    this.signal.dialogEnded.next(true);
  }
  
	private showNotepad() {
    if (!localStorage.getItem('resume') && (this.npsDetailData.speaker === SPEAKERS_KEYS.freid || this.npsDetailData.speaker === SPEAKERS_KEYS.witch)) {
      this.ui.setAction(GameAction.ShowResumeHint);
    }
	}
}
